/**
 * useEmitEvent — fire-and-forget SessionEvent emitter.
 */
import { useCallback } from 'react';
import type {
  SessionEventPayload,
  SessionEventCategory,
  Severity,
} from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';

export interface EmitEventInput {
  eventType: string;
  eventCategory: SessionEventCategory;
  severity?: Severity;
  cartId?: string;
  sessionId?: string;
  context?: string;
  payload?: Record<string, unknown>;
}

export function useEmitEvent(): (input: EmitEventInput) => void {
  const client = useApiClient();
  const { user } = useAuth();

  return useCallback((input: EmitEventInput): void => {
    const payload: SessionEventPayload = {
      eventType: input.eventType,
      eventCategory: input.eventCategory,
      severity: input.severity ?? 'info',
      ...(input.cartId ? { cartId: input.cartId } : {}),
      ...(input.sessionId ? { sessionId: input.sessionId } : {}),
      ...(user?.upn ? { actor: user.upn } : {}),
      ...(input.context ? { context: input.context } : {}),
      ...(input.payload ? { payloadJson: JSON.stringify(input.payload) } : {}),
      timestamp: new Date().toISOString() as SessionEventPayload['timestamp'],
    };
    client.retail.events.write(payload).catch(() => { /* swallow */ });
  }, [client, user]);
}
